import { useCallback, useEffect, useRef } from "react";
import type { FieldErrors } from "react-hook-form";

import type {
  FunnelStep,
  StepSelections,
  ValidationErrorMeta,
} from "~/shared/utils/types";
import {
  funnelTracker,
  startStep,
  trackStepBack,
  trackStepComplete,
  trackValidationError,
} from "../../utils/funnelTracking";

interface UseFunnelTrackingOptions {
  /** The step identifier for this form */
  step: FunnelStep;
  /** Whether to auto-start step timing on mount */
  autoStart?: boolean;
}

interface UseFunnelTrackingReturn {
  /** Call when form is successfully submitted, optionally with selection data */
  trackComplete: (selections?: StepSelections) => Promise<void>;
  /** Call when user navigates back */
  trackBack: () => Promise<void>;
  /** Call with react-hook-form errors to track validation failures */
  trackErrors: (errors: FieldErrors) => Promise<void>;
  /** Manually start step timing */
  startTracking: () => void;
}

/**
 * Hook for tracking funnel events in form components
 *
 * @example
 * ```tsx
 * const { trackComplete, trackBack, trackErrors } = useFunnelTracking({
 *   step: "health_conditions",
 * });
 *
 * const onSubmit = async (data) => {
 *   await trackComplete({ conditions: data.conditions });
 *   onNext();
 * };
 *
 * const handlePrev = async () => {
 *   await trackBack();
 *   onPrev();
 * };
 *
 * // In form validation
 * const handleSubmit = form.handleSubmit(onSubmit, (errors) => {
 *   trackErrors(errors);
 * });
 * ```
 */
export const useFunnelTracking = ({
  step,
  autoStart = true,
}: UseFunnelTrackingOptions): UseFunnelTrackingReturn => {
  const hasStartedRef = useRef(false);

  // Start step timing on mount
  useEffect(() => {
    if (autoStart && !hasStartedRef.current) {
      startStep(step);
      hasStartedRef.current = true;
    }
  }, [step, autoStart]);

  const startTracking = useCallback(() => {
    startStep(step);
    hasStartedRef.current = true;
  }, [step]);

  const trackComplete = useCallback(
    async (selections?: StepSelections) => {
      await trackStepComplete(step, selections);
    },
    [step],
  );

  const trackBack = useCallback(async () => {
    await trackStepBack(step);
  }, [step]);

  const trackErrors = useCallback(
    async (errors: FieldErrors) => {
      const validationErrors: ValidationErrorMeta[] = Object.entries(errors)
        .filter(([_, error]) => error)
        .map(([field, error]) => ({
          field,
          message: error?.message as string | undefined,
        }));

      if (validationErrors.length > 0) {
        await trackValidationError(step, validationErrors);
      }
    },
    [step],
  );

  return {
    trackComplete,
    trackBack,
    trackErrors,
    startTracking,
  };
};

/**
 * Hook for sub-step tracking (insurance, location flows)
 * Automatically prefixes the step with the parent step
 *
 * @example
 * ```tsx
 * const { trackComplete, trackBack, trackErrors } = useSubStepTracking({
 *   parentStep: "primary_insurance",
 *   subStep: "provider",
 * });
 * ```
 */
export const useSubStepTracking = ({
  parentStep,
  subStep,
  autoStart = true,
}: {
  parentStep: "primary_insurance" | "secondary_insurance" | "location";
  subStep: string;
  autoStart?: boolean;
}): UseFunnelTrackingReturn => {
  const step = `${parentStep}.${subStep}` as FunnelStep;
  return useFunnelTracking({ step, autoStart });
};

// Re-export tracker for direct access
export { funnelTracker };
